import React, { useState, useEffect, useMemo } from 'react';
import { ClientEntity, Showcase, ShowcaseItem } from '../../types';
import { Loader2, ExternalLink, ShieldAlert, Presentation as PresentationIcon, Link as LinkIcon, X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { db } from '../../../services/db';

interface PublicShowcaseProps {
  slug: string;
  clients: ClientEntity[];
  onClose?: () => void;
}

export const PublicShowcase: React.FC<PublicShowcaseProps> = ({ slug, clients, onClose }) => {
  const [showcase, setShowcase] = useState<Showcase | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState<string>('');
  const [fullScreenItem, setFullScreenItem] = useState<ShowcaseItem | null>(null);

  // Retrieve Showcase from Supabase Database
  useEffect(() => {
    const loadShowcase = async () => {
      setLoading(true);
      try {
        const found = await db.getShowcaseBySlug(slug);
        if (found) {
          setShowcase(found);
          const firstTab = found.items[0]?.tabCategory || '';
          setActiveTab(firstTab);
        }
      } catch (err) {
        console.error('Failed to load showcase from DB:', err);
      } finally {
        setLoading(false);
      }
    };
    loadShowcase();
  }, [slug]);

  const tabs = useMemo(() => {
    if (!showcase) return [];
    const t = new Set<string>();
    showcase.items.forEach(i => t.add(i.tabCategory));
    return Array.from(t);
  }, [showcase]);

  const currentTabItems = useMemo(() => {
    if (!showcase) return [];
    return showcase.items.filter(i => i.tabCategory === activeTab);
  }, [showcase, activeTab]);

  // If tab changes, close full screen
  useEffect(() => {
    setFullScreenItem(null);
  }, [activeTab]);

  // Secure iFrame Injection Logic for internals
  const resolveIframeData = (item: ShowcaseItem) => {
    if (item.type === 'external') {
      return { type: 'src', content: item.externalUrl };
    }
    const client = clients.find(c => c.id === item.clientId);
    if (!client) return { type: 'blocked', content: clients.length === 0 ? 'loading' : 'Base do site corrompida.' };

    const fixScript = `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
          document.body.addEventListener('click', function(e) {
            var target = e.target.closest('a');
            if (!target) return;
            var href = target.getAttribute('href');
            if (href && !href.startsWith('#')) {
               e.preventDefault(); e.stopPropagation(); 
               // Bloqueamos navegação para fora do portifólio
            }
          }, true);
        });
      </script>
    `;
    const html = client.htmlContent;
    const safeHtml = html.includes('</body>') ? html.replace('</body>', fixScript + '</body>') : html + fixScript;
    return { type: 'srcdoc', content: safeHtml };
  };

  if (loading) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;
  if (!showcase) return <div className="h-screen flex items-center justify-center bg-[#FAFAFA] text-gray-500 font-sans">Apresentação não encontrada ou expirada.</div>;

  return (
    <div className="flex flex-col h-screen bg-white font-sans text-gray-900 overflow-hidden">
      
      {/* HEADER DE NAVEGAÇÃO PREMIUM - MULTI-LEVEL */}
      <header className="shrink-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50 shadow-[0_4px_30px_rgba(0,0,0,0.02)] flex flex-col sticky top-0">
         {/* Top Bar: Logo & Brand */}
         <div className="h-14 flex items-center px-4 lg:px-8 justify-between">
            <div className="flex items-center gap-3 overflow-hidden">
               <div className="w-7 h-7 bg-black rounded-lg flex items-center justify-center shrink-0 shadow-lg shadow-black/20">
                  <span className="text-white font-black text-xs">N</span>
               </div>
               <div className="font-extrabold tracking-tighter text-sm lg:text-base truncate uppercase border-l border-gray-100 pl-3 text-black">
                  {showcase.name}
               </div>
            </div>
            {onClose && (
               <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-black transition-colors rounded-lg bg-gray-50 border border-gray-100" title="Fechar">
                  <X size={16} />
               </button>
            )}
         </div>

         {/* Navigation Bar: Compact Tabs */}
         <div className="h-10 flex items-center px-4 lg:px-8 border-t border-gray-50 overflow-x-auto no-scrollbar">
            <div className="flex gap-2">
               {tabs.map(tab => (
                  <button 
                    key={tab} 
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-1.2 text-[9px] font-black uppercase tracking-[0.12em] rounded-full transition-all whitespace-nowrap ${
                      activeTab === tab 
                        ? 'bg-black text-white shadow-lg shadow-black/10 ring-1 ring-black' 
                        : 'text-gray-400 hover:text-gray-900 bg-white border border-gray-100'
                    }`}
                  >
                    {tab === 'Links Extras' ? 'Outros' : tab}
                  </button>
               ))}
            </div>
         </div>
      </header>

      {/* ÁREA DE GRID DE CARDS */}
      <main className="flex-1 overflow-y-auto bg-[#FAFAFA] p-6 lg:p-12">
        <div className="max-w-7xl mx-auto">
           <h2 className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400 mb-8 flex items-center gap-3">
             <span className="w-8 h-[1px] bg-gray-200"></span>
             {activeTab === 'Links Extras' ? 'Outros' : activeTab}
           </h2>
           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {currentTabItems.map(item => {
                 const client = item.type === 'internal' ? clients.find(c => c.id === item.clientId) : null;
                 return (
                    <div 
                      key={item.id} 
                      onClick={() => setFullScreenItem(item)}
                      className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col"
                    >
                       <div className="h-40 relative bg-gray-50 border-b border-gray-100 overflow-hidden isolate">
                          {item.type === 'internal' && client ? (
                             <iframe 
                                srcDoc={client.htmlContent} 
                                className="absolute top-0 left-0 pointer-events-none bg-white border-0 z-0 select-none" 
                                style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }} 
                                title="preview" 
                                tabIndex={-1} 
                                sandbox="allow-scripts allow-same-origin" 
                             />
                          ) : item.type === 'external' ? (
                             <div className="w-full h-full relative bg-indigo-50/50 flex flex-col items-center justify-center">
                                <iframe 
                                   src={item.externalUrl} 
                                   className="absolute top-0 left-0 w-full h-full object-cover z-0 pointer-events-none bg-white border-0" 
                                   style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }}  
                                   title="external-preview"
                                   sandbox="allow-scripts allow-same-origin allow-popups"
                                />
                             </div>
                          ) : (
                             <div className="flex items-center justify-center w-full h-full text-gray-300">
                                <PresentationIcon size={40} />
                             </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                             <div className="w-12 h-12 rounded-full bg-white text-black shadow-lg flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300">
                               <Play size={20} className="ml-1" />
                             </div>
                          </div>
                       </div>
                       <div className="p-4 flex flex-col flex-1">
                          <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1">
                             {item.type === 'internal' ? client?.data.name : item.externalTitle}
                          </h3>
                          <p className="text-gray-500 text-xs truncate">
                             {item.type === 'internal' ? 'Layout Exclusivo' : item.externalUrl}
                          </p>
                       </div>
                    </div>
                 )
              })}
           </div>
        </div>
      </main>

      {/* OVERLAY FULLSCREEN */}
      {fullScreenItem && (() => {
         const currentIndex = currentTabItems.findIndex(i => i.id === fullScreenItem.id);
         const hasPrev = currentIndex > 0;
         const hasNext = currentIndex < currentTabItems.length - 1;
         const iframeData = resolveIframeData(fullScreenItem);

         return (
            <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
               {/* Overlay Header */}
               <header className="h-14 bg-black text-white flex items-center justify-between px-4 shrink-0 shadow-lg relative z-20">
                  <div className="flex items-center gap-4 flex-1">
                     <button onClick={() => setFullScreenItem(null)} className="p-2 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white">
                        <X size={20} /> <span className="hidden sm:inline">Fechar Preview</span>
                     </button>
                     <div className="h-4 w-px bg-gray-700 hidden sm:block"></div>
                     <span className="hidden sm:inline text-sm font-medium text-gray-400 truncate">
                        {fullScreenItem.type === 'internal' ? clients.find(c => c.id === fullScreenItem.clientId)?.data.name : fullScreenItem.externalTitle}
                     </span>
                  </div>

                  <div className="flex items-center gap-2">
                     <button 
                       onClick={() => setFullScreenItem(currentTabItems[currentIndex - 1])}
                       disabled={!hasPrev}
                       className="p-2 hover:bg-white/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                     >
                       <ChevronLeft size={20} />
                     </button>
                     <span className="text-xs font-mono text-gray-400 w-12 text-center">{currentIndex + 1} / {currentTabItems.length}</span>
                     <button 
                       onClick={() => setFullScreenItem(currentTabItems[currentIndex + 1])}
                       disabled={!hasNext}
                       className="p-2 hover:bg-white/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                     >
                       <ChevronRight size={20} />
                     </button>
                  </div>
               </header>

               {/* Overlay Content */}
               <div className="flex-1 relative bg-gray-100">
                 {iframeData.type === 'src' ? (
                    <div className="w-full h-full relative">
                       <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 bg-[radial-gradient(#d1d5db_1px,transparent_1px)] [background-size:20px_20px]">
                          <ShieldAlert size={48} className="text-gray-300 mb-4" />
                          <p className="text-gray-500 font-medium mb-4 text-center max-w-sm">Esta é uma referência externa. Devido as barreiras de segurança do fabricante, abra no navegador para testar a experiência completa.</p>
                          <a href={iframeData.content} target="_blank" rel="noreferrer" className="bg-black text-white px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform shadow-xl">
                             Abrir Site em Nova Aba <ExternalLink size={16} />
                          </a>
                       </div>
                       <iframe src={iframeData.content} className="absolute inset-0 w-full h-full border-0 z-10 bg-white" title={fullScreenItem.externalTitle} />
                    </div>
                 ) : iframeData.type === 'srcdoc' ? (
                    <iframe 
                      key={fullScreenItem.id} 
                      srcDoc={iframeData.content} 
                      title="Nyroh Internal Site" 
                      className="w-full h-full border-0 bg-white animate-in zoom-in-95 duration-500" 
                      sandbox="allow-scripts allow-same-origin"
                    />
                 ) : iframeData.type === 'blocked' && iframeData.content === 'loading' ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400"><Loader2 className="animate-spin mb-4" size={40} /><p>Carregando ambiente...</p></div>
                 ) : (
                    <div className="p-8 text-center text-red-500">Falha ao decodificar item da vitrine.</div>
                 )}
               </div>
            </div>
         );
      })()}

    </div>
  );
};
